import { buildLegacyTrackingProperties } from '@render/composables/usePaymentEvents'
import { PIPE_EVENTS, type PipeInterface } from '@render/composables/usePipe'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PaymentDataRecord = Record<string, any>

export type PaymentDataOptions = {
  pipelineOfferType?: string
  priceProductObjectId?: string | null
  restrictSaveCard?: boolean
  // if it's undefined backend decides which provider to use (e.g Primer / Yuno) default = primer
  provider?: string
}

// Hardcoded preview payload used in editor mode
export const PAYMENT_DATA_PREVIEW: PaymentDataRecord = {
  priceProductObjectId: '4ef574e7-760a-470c-9485-11b966194951',
  pipelineOfferType: 'BASIC',
  brandCode: 'nm',
  provider: 'primer',
  currencyCode: 'USD',
  source: 'pipehouse',
  customerId: '2b111d74-1f7a-4a0b-bf09-ce23896dffb3',
  isTestPayment: true,
}

export function usePaymentData(pipe: Pick<PipeInterface, 'getData' | 'emit'>, isBrowser: boolean) {
  function getQueryParams(): PaymentDataRecord {
    try {
      return (pipe.getData('var.global.trackingProperties.queryParams') as PaymentDataRecord) ?? {}
    } catch {
      return {}
    }
  }

  function getCookies(): PaymentDataRecord {
    try {
      return (pipe.getData('var.global.trackingProperties.cookies') as PaymentDataRecord) ?? {}
    } catch {
      return {}
    }
  }

  function buildOriginUrl(): string {
    if (typeof window === 'undefined') return ''

    const originUrl = window.location.href
    if (!originUrl) return ''

    const url = new URL(originUrl as string)
    url.search = ''
    url.hash = ''

    const segs = url.pathname.split('/').filter(Boolean)
    url.pathname =
      segs.length >= 2 ? `/${segs[0]}/${segs[1]}/` : segs.length === 1 ? `/${segs[0]}/` : '/'

    return url.toString()
  }

  function buildRealPaymentData({
    pipelineOfferType,
    priceProductObjectId,
    restrictSaveCard,
    provider,
  }: PaymentDataOptions): PaymentDataRecord {
    // Refresh the snapshot's cookie half before reading it — fbp/fbc/gaCid all
    // come from cookies the tag scripts write after the landing capture. The
    // handler runs inline (see PIPE_EVENTS.trackingRefresh); no listener when
    // TRACKING_PROPERTIES is disabled.
    //
    // Read the snapshot rather than `document.cookie`: payments-api and the
    // order's rawData must carry identical values (the CAPI purchase event
    // dedupes against them), and the snapshot also holds the fbclid-derived
    // `_fbc` that no live cookie read can produce.
    if (isBrowser) pipe.emit(PIPE_EVENTS.trackingRefresh)

    const queryParams = isBrowser ? getQueryParams() : {}
    const cookies = isBrowser ? getCookies() : {}
    const originUrl = isBrowser ? buildOriginUrl() : ''

    const retrieveGaCid = () => cookies._ga?.match(/[^\.]+\.[^\.]+$/)?.[0]
    const retrieveGaSessionid = () => cookies._ga?.split('.')[2]

    return {
      brandCode: pipe.getData('const.system.brandCode'),
      provider,
      source: 'pipehouse',
      customerId: pipe.getData('var.global.paymentsApiCustomerId'),
      priceProductObjectId,
      pipelineOfferType,
      currencyCode: (pipe.getData('const.system.currency') as string)?.toUpperCase(),
      originUrl,
      orderId: (pipe.getData('var.global.orderId') as string) || '',
      orderToken: (pipe.getData('var.global.orderId') as string) || '',
      fbp: cookies?._fbp || '',
      fbc: cookies?._fbc || '',
      gaCid: retrieveGaCid() || '',
      gaSessionId: retrieveGaSessionid() || '',
      fraudNetSessionId: cookies?._fraud_net_session_id || '',

      utmCampaign: queryParams?.utm_campaign || '',
      utmSource: queryParams?.utm_source || '',
      utmMedium: queryParams?.utm_medium || '',
      utmTerm: queryParams?.utm_term || '',
      utmContent: queryParams?.utm_content || '',

      restrictSaveCard,
      trackingProperties: isBrowser ? buildLegacyTrackingProperties(pipe) : {},

      // Allows payments-api to skip its webhook callback to Pipehouse
      isTestPayment: (pipe.getData('const.system.appEnv') as string | undefined) === 'development',
    }
  }

  return { buildRealPaymentData }
}
